/**
 * VAL-INTAKE-SCOPE-1 §3 — the "I'm not sure" control and its two routings.
 *
 * Split out of `_DriverQuestionnaire.tsx` to keep that file under the
 * FILE_SIZE_POLICY caution threshold as this grew. Owns nothing the parent
 * needs for scoring — `deferred` / `attachment` state lives in the parent
 * (`_DriverQuestionnaire.tsx`) and is never sent to the API; the wire payload
 * stays exactly `question_id -> option value`, unchanged by any of this
 * (R3d: "the engine rule does not change").
 *
 * R3a — "I'm not sure" is a separate CONTROL, not an answer option. It never
 * appears among `question.options` and never scores.
 *
 * R3b/R3g — routing reads `question.ask_or_derive` at render time. There is
 * no question-id list here or anywhere downstream of this file (S19).
 *
 *   Branch 1 — document-answerable (`ask_or_derive` contains "DERIVE"):
 *   deferring makes an upload MANDATORY (R3c) and disables the answer radios
 *   until a document is attached (R3e/S18 fence — a deferred question cannot
 *   be resolved by hand). The document is named by showing the question's own
 *   `help_text` back, which already says what settles it — never a
 *   per-question hardcoded document name.
 *
 *   Branch 2 — owner-only (no "DERIVE" in `ask_or_derive`): deferring shows
 *   conservative-reading guidance ONLY. No upload control exists in this
 *   branch at all (R3f/S16 fence) — the radios are never disabled, because
 *   there is nothing to wait on; the seller answers directly.
 */
import { useRef } from 'react'
import type { DriverQuestion } from '@/types/valuation'

/** Client-side only until a real document-intake endpoint exists
 * (CLAUDE.md: valuation document intake is a separate, not-yet-built brief).
 * Holding just the filename is enough to satisfy R3c/R3e's UI-level
 * requirements without inventing storage this brief was not asked to build. */
export type DeferredAttachment = { name: string }

export function isDocumentAnswerable(question: DriverQuestion): boolean {
  return question.ask_or_derive.includes('DERIVE')
}

export function DeferControl({
  question,
  deferred,
  attachment,
  onDefer,
  onCancelDefer,
  onAttach,
}: {
  question: DriverQuestion
  deferred: boolean
  attachment: DeferredAttachment | undefined
  onDefer: (questionId: string) => void
  onCancelDefer: (questionId: string) => void
  onAttach: (questionId: string, file: DeferredAttachment) => void
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const docBranch = isDocumentAnswerable(question)

  if (!deferred) {
    return (
      <button
        type="button"
        onClick={() => onDefer(question.question_id)}
        className="mb-2 text-xs font-medium text-pal-ink-muted underline underline-offset-2 hover:text-pal-ink-body"
      >
        I&apos;m not sure
      </button>
    )
  }

  // Branch 2 — owner-only, R3f: guidance only, never a document path.
  if (!docBranch) {
    return (
      <div className="mb-3 rounded-md border border-amber-200 bg-pal-warning-bg p-2 text-xs text-pal-warning-text">
        <p>
          No document settles this one — it's a judgment call about the business itself. Pick the
          closest reading below; when you're between two options, we score the lower one.
        </p>
        <button
          type="button"
          onClick={() => onCancelDefer(question.question_id)}
          className="mt-1 underline underline-offset-2 hover:text-pal-pill-warning-text"
        >
          Got it
        </button>
      </div>
    )
  }

  // Branch 1 — document-answerable, R3c/R3e: mandatory upload; the document
  // is named by showing help_text back, not a hardcoded per-question label.
  if (!attachment) {
    return (
      <div className="mb-3 rounded-md border border-amber-200 bg-pal-warning-bg p-2 text-xs text-pal-warning-text">
        <p className="font-medium">A document has to settle this one — we don't record a guess.</p>
        {question.help_text && <p className="mt-1">{question.help_text}</p>}
        <div className="mt-2 flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="text-xs"
            onChange={e => {
              const file = e.target.files?.[0]
              if (file) onAttach(question.question_id, { name: file.name })
            }}
          />
        </div>
        {/* VAL-PATH P5 / I29 — only the file name is kept until document
            upload is built (queue item); say so. */}
        <p className="mt-1">File attached by name only</p>
        <button
          type="button"
          onClick={() => onCancelDefer(question.question_id)}
          className="mt-2 underline underline-offset-2 hover:text-pal-pill-warning-text"
        >
          Answer directly instead
        </button>
      </div>
    )
  }

  // Document attached — R3e: the answer is now confirmed FROM the document,
  // not typed blind. Radios re-enable in the parent for this question only
  // because an attachment exists.
  return (
    <div className="mb-3 rounded-md border border-pal-info-border bg-pal-info-bg p-2 text-xs text-pal-pill-info-text">
      <p>
        Reviewed: <span className="font-medium">{attachment.name}</span>. Confirm the option the
        document supports below — this is a confirmation, not an estimate.
      </p>
    </div>
  )
}
